import { useEffect, useState } from "react";

import { MedplumClient } from "@medplum/core";
import { ValueSetExpansionContains } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Accordion, ActionIcon, Checkbox, Group, Stack, Tooltip } from "@mantine/core";
import { FormArrayElement, UseFormReturnType } from "@mantine/form";

import { IconInfoCircle } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { MostCommonSectionConfig } from "@/encounters/constants/procedures";
import {
  loadQuickPickerCodings,
  QuickPickerCoding,
} from "@/encounters/services/quick-picker-codes";
import { ProceduresPickerFormType } from "@/encounters/zod-schemas/procedures";
import { getValueSetOptions } from "@/utils/medplum";
import { toast } from "@/utils/notifications";

import ProceduresCodeBadge from "./ProceduresCodeBadge";

/** The two array-of-coding fields this picker writes into. */
export type ProceduresPickerField = "conditions" | "procedures";

/**
 * Selection identity for a coding. We key on `code` alone (not `system`) so a
 * code picked from the CodingInput — whose ValueSet tags it with a different
 * system than the curated "most common" ValueSets — stays connected to its
 * checkbox instead of duplicating. Within a single tab the field is homogeneous
 * (ICD-10 for conditions, CPT for procedures), so the code is unambiguous.
 */
function codingKey(coding: { code?: string }): string {
  return coding.code ?? "";
}

/** The accordion value for a section: its ValueSet or CodeSystem URL. */
export function getMostCommonSectionId(section: MostCommonSectionConfig): string {
  return "codeSystem" in section ? section.codeSystem : section.valueSet;
}

function loadSectionCodings(
  medplum: MedplumClient,
  section: MostCommonSectionConfig,
): Promise<QuickPickerCoding[]> {
  return "codeSystem" in section
    ? loadQuickPickerCodings(medplum, section.codeSystem)
    : getValueSetOptions(medplum, section.valueSet);
}

type MostCommonSectionProps<K extends ProceduresPickerField> = {
  /** Where the section's codes come from: a seeded ValueSet, or a fee schedule CodeSystem. */
  section: MostCommonSectionConfig;
  form: UseFormReturnType<ProceduresPickerFormType>;
  name: K;
  /**
   * Whether the parent accordion has this section expanded. The codes are
   * loaded lazily on first open so a picker with dozens of sections doesn't
   * fire a request per section on mount.
   */
  opened: boolean;
  /** Read-only: the section still lists its codes but they cannot be toggled. */
  disabled?: boolean;
  /**
   * Builds the form entry stored when a code is checked. Conditions store the
   * bare coding; procedures wrap it with biller/provider notes, units and
   * modifiers, so the caller owns the shape. This is also the boundary that
   * coerces the optional expansion fields into the required form coding.
   */
  buildEntry: (coding: ValueSetExpansionContains) => FormArrayElement<ProceduresPickerFormType, K>;
  /** Lists only the codes it returns true for. */
  filterOption?: (coding: ValueSetExpansionContains) => boolean;
};

/**
 * One accordion section of "most common" codes, from a seeded ValueSet or from
 * the quick picker codes of a fee schedule CodeSystem. Lists the codes as
 * checkboxes and adds/removes the selected coding to/from the given form field
 * as it is toggled. A code with Info box text shows it in a tooltip.
 */
function MostCommonSection<K extends ProceduresPickerField>({
  section,
  form,
  name,
  opened,
  disabled = false,
  buildEntry,
  filterOption,
}: MostCommonSectionProps<K>) {
  const medplum = useMedplum();
  const [options, setOptions] = useState<QuickPickerCoding[]>([]);
  const { title } = section;
  const sectionId = getMostCommonSectionId(section);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (!opened || hasLoaded) return;

    let active = true;
    const fetchOptions = async () => {
      try {
        const results = await loadSectionCodings(medplum, section);
        if (!active) return;
        setOptions(results);
        setHasLoaded(true);
      } catch {
        if (!active) return;
        toast({
          type: "error",
          title: "Failed to load codes",
          message: `Could not load the "${title}" section. Close and reopen it to retry.`,
          autoClose: false,
        });
      }
    };

    fetchOptions();
    return () => {
      active = false;
    };
  }, [opened, hasLoaded, medplum, section, title]);

  const selected = form.getValues()[name];
  const visibleOptions = filterOption ? options.filter(filterOption) : options;

  const handleToggle = (coding: ValueSetExpansionContains) => {
    const key = codingKey(coding);
    const index = selected.findIndex((item) => codingKey(item) === key);

    if (index >= 0) {
      form.removeListItem(name, index);
    } else {
      form.insertListItem(name, buildEntry(coding));
    }
  };

  return (
    <Accordion.Item value={sectionId}>
      <Accordion.Control>{title}</Accordion.Control>
      <Accordion.Panel>
        <Stack gap="sm">
          {visibleOptions.map((coding) => {
            const isSelected = selected.some((item) => codingKey(item) === codingKey(coding));

            return (
              <Group gap="sm" wrap="nowrap" key={codingKey(coding)}>
                <Checkbox
                  disabled={disabled}
                  checked={isSelected}
                  onChange={() => handleToggle(coding)}
                  data-testid={`most-common-checkbox-${coding.code ?? ""}`}
                />
                <ProceduresCodeBadge code={coding.code ?? ""} />
                <TextWrapper size="sm" c="gray.7">
                  {coding.display}
                </TextWrapper>
                {coding.information ? (
                  <Tooltip
                    w={320}
                    multiline
                    withArrow
                    label={coding.information}
                    events={{ hover: true, focus: true, touch: true }}
                  >
                    <ActionIcon
                      size="sm"
                      color="gray.7"
                      variant="subtle"
                      aria-label={`Info for ${coding.code ?? ""}`}
                      data-testid={`most-common-info-${coding.code ?? ""}`}
                    >
                      <IconInfoCircle size={16} />
                    </ActionIcon>
                  </Tooltip>
                ) : null}
              </Group>
            );
          })}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

export default MostCommonSection;
